<template>
  <div id="app" @click="clicked">
    <div class="watermark-overlay" v-if="watermarkImage" :style="watermarkStyle"></div>
    <Loading />

    <!-- 全域公告對話框（登入後顯示） -->
    <el-dialog title="最新公告" :visible.sync="announcementDialogVisible" :close-on-click-modal="false" :show-close="false" width="50%" custom-class="announcement-dialog" @opened="onAnnouncementDialogOpened">
      <div class="announcement-container" @scroll="onAnnouncementScroll">
        <div v-if="announcements.length === 0" style="text-align: center; padding: 40px 20px;">
          <p>暫無公告</p>
        </div>
        <el-card v-for="(ann, idx) in announcements" :key="idx" style="margin-bottom: 15px;">
          <div slot="header" class="clearfix">
            <span style="font-weight: bold; font-size: 16px;">{{ ann.Title }}</span>
          </div>
          <div style="white-space: pre-wrap; line-height: 1.6;">{{ ann.Content }}</div>
        </el-card>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" :disabled="!announcementScrolledToBottom" @click="confirmAnnouncement" style="width:100%;">我已知悉，進入系統</el-button>
      </span>
    </el-dialog>

    <div id="nav">
      <el-aside v-if="!$route.matched.some((r) => r.meta && r.meta.hideMenu)" class="mainLayoutAside" :class="{ isCollapse: isCollapse }">
        <div class="mainLayoutAside__title" :class="{ mainLayoutAside__shrink: isCollapse }" :style="{ 'flex-direction: column': !isCollapse }">
          <img :src="logoImg" alt="logo" :style="getLogoWidth" />
          <i class="el-icon-s-unfold" :class="{ goRound: !isCollapse }" @click="isCollapse = !isCollapse"></i>
        </div>
        <div class="unWrap" v-show="!isCollapse">
          <strong>{{ getUserProfile ? getUserProfile : "" }} 您好</strong>
        </div>
        <el-menu :default-active="$route.name" :show-timeout="200" :collapse-transition="false" :unique-opened="uniqueOp" :collapse="isCollapse" background-color="#191970" text-color="#fff" active-text-color="#fff" :router="false">
          <Sidebar v-for="menu in routes" :key="menu.id" :isCollapse="isCollapse" :item="menu" @doRoute="doRoute" />
        </el-menu>
      </el-aside>

      <el-main :class="{ withUp: isCollapse, noBar: $route.matched.some((r) => r.meta && r.meta.hideMenu) }">
        <Breadcrumb v-if="!isTabletDevice() && !$route.matched.some((r) => r.meta && r.meta.hideBreadcrumb)" :breadcrumbs="breadcrumbs" />
        <div class="viewHeight">
          <router-view></router-view>
        </div>
      </el-main>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Loading from "./components/Loading.vue";
import Sidebar from "./components/Sidebar.vue";
import Breadcrumb from "./components/Breadcrumb.vue";
import { getBreadcrumbsByRouteName } from "./utils/menuHelper";
import { isTablet } from "./utils/deviceHelper";

export default {
  components: { Loading, Sidebar, Breadcrumb },
  data() {
    return {
      watermarkImage: "",
      isCollapse: false,
      uniqueOp: true,
      transitionName: "slide-left",
      logoImg: require("../src/assets/logo.png"),
      lTime: new Date().getTime(), // 最後一次活動時間
      tOut: 10 * 60 * 1000, // 超時時間 10 分鐘
      timeID: "",
      ctimeID: "",
      watermarkIntervalID: "",
      activityHandler: null,
      storageActivityHandler: null,
      breadcrumbs: [],
      // 公告全域狀態
      announcementDialogVisible: false,
      announcements: [],
      announcementScrolledToBottom: false,
      announcementLoadedInSession: false,
    };
  },
  watch: {
    "$route.name"(newRouteName) {
      if (this.$route.meta && this.$route.meta.customBreadcrumbs) {
        this.breadcrumbs = this.$route.meta.customBreadcrumbs;
      } else {
        const result = getBreadcrumbsByRouteName(newRouteName);
        this.breadcrumbs = Array.isArray(result) ? result : [{ name: "首頁", icon: "home", path: "home" }];
      }
    },
    currentToken(newToken) {
      if (newToken) {
        this.loadWatermark();
        if (!this.announcementLoadedInSession) {
          this.announcementLoadedInSession = true;
          if (!this.hasAnnouncementShownInBrowser()) {
            this.fetchAnnouncements();
          }
        }
      } else {
        this.watermarkImage = "";
        this.announcementLoadedInSession = false;
        this.clearAnnouncementShownInBrowser();
      }
    },
  },
  methods: {
    isTabletDevice() {
      const forceTablet = String(process.env.VUE_APP_FORCE_TABLET || "").toLowerCase();
      if (["1", "true", "yes", "y", "on"].includes(forceTablet)) {
        return true;
      }
      return isTablet();
    },

    // Announcement helpers
    getAnnouncementStorageKey() {
      const userId = (this.$store.state.userInfo && this.$store.state.userInfo.UserID) || "anonymous";
      return `announcement_shown_${userId}`;
    },
    hasAnnouncementShownInBrowser() {
      try {
        const key = this.getAnnouncementStorageKey();
        return !!window.localStorage.getItem(key);
      } catch (e) {
        return false;
      }
    },
    markAnnouncementShownInBrowser() {
      try {
        const key = this.getAnnouncementStorageKey();
        window.localStorage.setItem(key, String(Date.now()));
      } catch (e) {
        // ignore
      }
    },
    clearAnnouncementShownInBrowser() {
      try {
        const key = this.getAnnouncementStorageKey();
        window.localStorage.removeItem(key);
      } catch (e) {
        // ignore
      }
    },

    onAnnouncementDialogOpened() {
      this.setupAnnouncementDialogLayout();
      this.initAnnouncementScroll();
    },
    setupAnnouncementDialogLayout() {
      this.$nextTick(() => {
        const dialog = this.$el.querySelector(".announcement-dialog");
        const wrapper = dialog && dialog.parentElement;
        if (wrapper && wrapper.classList.contains("el-dialog__wrapper")) {
          wrapper.style.display = "flex";
          wrapper.style.alignItems = "center";
          wrapper.style.justifyContent = "center";
          wrapper.style.padding = "16px";
        }
      });
    },
    async fetchAnnouncements() {
      try {
        let nowText = "";
        if (this.$dayjs) {
          nowText = this.$dayjs().format("YYYY-MM-DDTHH:mm:ss");
        } else {
          const now = new Date();
          const pad = (v) => String(v).padStart(2, "0");
          nowText = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
        }
        const payload = { Search_Time: nowText, Page_Num: 1, Page_Size: 100, isSys: true };
        const response = await this.$api.apiannoun.searchAnnouncement(payload);
        if (response && response.data) {
          this.announcements = Array.isArray(response.data) ? response.data : [];
          if (this.announcements.length > 0) {
            // 記錄為已顯示，避免開新分頁再次彈出
            this.markAnnouncementShownInBrowser();
            this.announcementScrolledToBottom = false;
            this.announcementDialogVisible = true;
            this.$nextTick(() => this.initAnnouncementScroll());
          }
        }
      } catch (e) {
        console.error("fetchAnnouncements error", e);
      }
    },
    initAnnouncementScroll() {
      this.$nextTick(() => {
        const container = this.$el.querySelector(".announcement-container");
        if (container) this.checkAnnouncementScroll(container);
      });
    },
    onAnnouncementScroll(event) {
      const container = event.target;
      this.checkAnnouncementScroll(container);
    },
    checkAnnouncementScroll(container) {
      if (!container) return;
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= 10;
      this.announcementScrolledToBottom = isAtBottom;
    },
    confirmAnnouncement() {
      this.announcementDialogVisible = false;
      this.announcements = [];
      this.announcementScrolledToBottom = false;
    },

    // Navigation & session helpers
    doRoute(val) {
      this.$router.push({ name: val });
    },
    clicked() {
      this.recordActivity();
    },
    getActivityStorageKey() {
      const userId = (this.$store.state.userInfo && this.$store.state.userInfo.UserID) || window.localStorage.getItem("userID") || "anonymous";
      return `last_activity_${userId}`;
    },
    getStoredActivityTime() {
      try {
        const stored = window.localStorage.getItem(this.getActivityStorageKey());
        const parsed = Number(stored);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      } catch (e) {
        return null;
      }
    },
    syncActivityFromStorage() {
      const storedTime = this.getStoredActivityTime();
      if (storedTime && storedTime > this.lTime) {
        this.lTime = storedTime;
      }
    },
    onActivityStorageChange(event) {
      if (!event || event.key !== this.getActivityStorageKey()) return;
      const newTime = Number(event.newValue);
      if (Number.isFinite(newTime) && newTime > this.lTime) {
        this.lTime = newTime;
      }
    },
    recordActivity() {
      const now = Date.now();
      this.lTime = now;
      try {
        window.localStorage.setItem(this.getActivityStorageKey(), String(now));
      } catch (e) {
        // ignore localStorage write failures
      }
    },
    tTime() {
      if (!(this.$store.state.userInfo && this.$store.state.userInfo.UserID)) {
        return;
      }
      this.syncActivityFromStorage();
      const cTime = new Date().getTime();
      const diff = cTime - this.lTime;
      if (diff > this.tOut) {
        console.log(this.timeID);
        this.$store.dispatch("Logout").then(() => {
          window.localStorage.removeItem("userID");
          this.$notify({
            title: "自動登出",
            message: "您已超過10分鐘未使用，因此自動登出!",
            type: "success",
          });

          if (this.timeID) {
            window.clearInterval(this.timeID);
          } else {
            console.log("TimeID is not exist");
          }

          this.$router.push("/logoutauto");
        });
      }
    },
    checkUser() {
      let userInfo = this.$store.state.userInfo;
      if (userInfo && userInfo.UserID) {
        this.$store
          .dispatch("RefreshSessionToken")
          .then((success) => {
            if (!success) {
              window.clearInterval(this.ctimeID);
              window.clearInterval(this.timeID);
              this.$router.push("/logoutauto?msg=sameuser");
            }
          })
          .catch((err) => {
            window.clearInterval(this.ctimeID);
            console.log("refresh_token fail");
            console.log(err);
            this.$router.push("/logoutauto?msg=sameuser");
          });
      }
    },
    async loadWatermark() {
      if (this.$store.state.token == null || this.$store.state.token == "") return;
      try {
        const response = await this.$api.watermark.get_watermark({});
        if (response.data && response.data.image) {
          this.watermarkImage = response.data.image;
        }
      } catch (error) {
        console.error("載入全站浮水印失敗:", error);
      }
    },
  },
  computed: {
    watermarkStyle() {
      if (!this.watermarkImage) return {};
      return {
        backgroundImage: `url(${this.watermarkImage})`,
        backgroundRepeat: "repeat",
        backgroundPosition: "center",
        backgroundSize: "400px 300px",
        opacity: "0.25",
      };
    },
    ...mapGetters(["userInfo"]),
    currentToken() {
      return this.$store.state.token;
    },
    getUserProfile: {
      get() {
        return (this.$store.state.userInfo && this.$store.state.userInfo.UserName) || "";
      },
    },
    getLogoWidth() {
      let sty = "width:30px;margin-bottom:20px;margin-top:20px;";
      if (!this.isCollapse) sty = sty + "margin-left:70px;width:100px;margin-bottom:10px;";
      return sty;
    },
    routes() {
      return this.$store.state.menuList;
    },
  },
  mounted() {
    const result = getBreadcrumbsByRouteName(this.$route.name);
    this.breadcrumbs = Array.isArray(result) ? result : [{ name: "首頁", icon: "home", path: "home" }];

    this.activityHandler = () => this.recordActivity();
    ["click", "keydown", "mousemove", "scroll", "touchstart"].forEach((eventName) => {
      window.addEventListener(eventName, this.activityHandler);
    });

    this.storageActivityHandler = (event) => this.onActivityStorageChange(event);
    window.addEventListener("storage", this.storageActivityHandler);
    this.syncActivityFromStorage();
    this.recordActivity();

    this.loadWatermark();
    if (this.currentToken && !this.announcementLoadedInSession) {
      this.announcementLoadedInSession = true;
      if (!this.hasAnnouncementShownInBrowser()) {
        this.fetchAnnouncements();
      }
    }

    this.watermarkIntervalID = window.setInterval(() => {
      this.loadWatermark();
    }, 60 * 1000);

    this.timeID = window.setInterval(this.tTime, 60 * 1000);
    this.ctimeID = window.setInterval(this.checkUser, 60000);

    if (top.location !== self.location) {
      top.location = self.location.href;
    }
  },
  beforeDestroy() {
    if (this.timeID) window.clearInterval(this.timeID);
    if (this.ctimeID) window.clearInterval(this.ctimeID);
    if (this.watermarkIntervalID) window.clearInterval(this.watermarkIntervalID);
    if (this.activityHandler) {
      ["click", "keydown", "mousemove", "scroll", "touchstart"].forEach((eventName) => {
        window.removeEventListener(eventName, this.activityHandler);
      });
    }
    if (this.storageActivityHandler) {
      window.removeEventListener("storage", this.storageActivityHandler);
    }
  },
};
</script>

<style lang="scss">
.announcement-dialog {
  width: min(920px, 92vw) !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;

  .el-dialog__body {
    flex: 1 1 auto;
    overflow: hidden;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .el-dialog__footer {
    flex: 0 0 auto;
    padding-top: 8px;
  }
}

.announcement-container {
  max-height: calc(88vh - 220px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 768px) {
  .announcement-dialog {
    width: 94vw !important;
    max-height: 90vh;
  }

  .announcement-container {
    max-height: calc(90vh - 210px);
  }
}

.watermark-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  pointer-events: none;
}

#nav {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: flex-start;
  .unWrap {
    padding-left: 20px;
  }
  .mainLayoutAside {
    padding-top: 10px;
    position: fixed;
    z-index: 2;
    height: 100vh;
    background: #191970;
    color: #fff;
    transition: 0.5s;
    width: 250px !important;

    &.isCollapse {
      width: 72px !important;
    }

    &__title {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      strong {
        font-size: 24px;
        color: #fff;
      }

      i {
        padding: 0 8px;
        font-size: 24px;
        margin-left: auto;
        transition: 0.5s;
        cursor: pointer;

        &.goRound {
          transform: rotate(180deg);
        }
      }
    }

    &__shrink {
      flex-direction: column;

      i {
        font-size: 20px;
        margin-left: 0;
      }
    }
  }

  .el-main {
    padding: 0;
    height: 100%;
    margin-left: 250px;

    &.withUp {
      margin-left: 72px;
    }

    &.noBar {
      margin-left: 0 !important;
    }
  }
}
.el-menu {
  &--collapse {
    width: 72px !important;
  }
}
</style>
